import { Checkbox, TextLink, Tooltip } from '@civilgrid/ui/components';
import classNames from 'classnames';
import { useId } from 'react';
import Icon from '~/components2/Icon';

/**
 * Picker row checkbox: the `@civilgrid/ui` Checkbox (controlled, tri-state) with the
 * legacy row's label layout. Parents derive `checked`/`'indeterminate'` from their
 * descendant leaves; leaves are plain booleans. Clicking an indeterminate parent checks
 * it (radix: indeterminate → true). With `labelHref`, the label text is a link that opens in a new tab,
 * truncated to one line with the full text in a tooltip, and the checkbox is named by `aria-label` instead.
 */
export function PickerCheckbox({
  checked,
  disabled = false,
  label,
  labelSuffix,
  labelClassName,
  labelHref,
  labelLinkTestId,
  onLabelLinkClick,
  onCheckedChange,
  'data-testid': testId,
}: {
  checked: boolean | 'indeterminate';
  disabled?: boolean;
  label: string;
  labelSuffix?: string;
  labelClassName?: string;
  labelHref?: string;
  labelLinkTestId?: string;
  onLabelLinkClick?: () => void;
  onCheckedChange: (value: boolean | 'indeterminate') => void;
  'data-testid'?: string;
}) {
  const checkboxId = useId();
  return (
    <div className={classNames('flex grow items-center gap-2', labelHref && 'min-w-0')}>
      <Checkbox
        id={checkboxId}
        checked={checked}
        disabled={disabled}
        aria-label={labelHref ? [label, labelSuffix].filter(Boolean).join(' · ') : undefined}
        data-testid={testId}
        onCheckedChange={onCheckedChange}
      />
      {labelHref ? (
        <span className={classNames('flex min-w-0 grow items-center text-xs', labelClassName)}>
          <Tooltip asChild text={label}>
            <TextLink
              href={labelHref}
              target="_blank"
              data-testid={labelLinkTestId ?? `${testId ?? 'picker-checkbox'}-link`}
              onClick={onLabelLinkClick}
              aria-label={`${label}, opens in a new tab`}
              className="inline-flex min-w-0 items-center gap-1"
            >
              <span className="truncate">{label}</span>
              <Icon icon="externalLink" size={12} className="shrink-0" />
            </TextLink>
          </Tooltip>
          {labelSuffix && <span className="shrink-0 whitespace-pre text-secondary"> · {labelSuffix}</span>}
        </span>
      ) : (
        <label htmlFor={checkboxId} className={classNames('grow cursor-pointer text-primary text-xs', labelClassName)}>
          {label}
          {labelSuffix && <span className="text-secondary"> · {labelSuffix}</span>}
        </label>
      )}
    </div>
  );
}
